import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { productsApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { ErrorNote, MatchBadge, PageHeader } from '@/components/common/Ui'

const EXAMPLES = [
  '50 kg 13 gauge gi wire',
  '2 inch 12 gauge wire nail',
  'binding wire 18swg',
  'jali 12 swg 2 bundle',
  '1.5 inch concrete keel 5 box',
]

/**
 * Lets staff check how the matcher reads a phrase, without sending themselves
 * WhatsApp messages. The fastest way to find shorthand worth adding as an
 * alias is to type what a customer actually said and watch it miss.
 */
export default function MatchTester() {
  const [query, setQuery] = useState('')
  const [error, setError] = useState('')

  const test = useMutation({
    mutationFn: (q) => productsApi.matchTest(q),
    onError: (e) => setError(errorMessage(e)),
  })

  const run = (value) => {
    const q = (value ?? query).trim()
    if (!q) return
    setQuery(q)
    setError('')
    test.mutate(q)
  }

  const result = test.data

  return (
    <div className="match-tester">
      <PageHeader
        title="Match tester"
        subtitle="See how a customer's wording resolves against the catalog"
      />

      <form
        className="tester-form"
        onSubmit={(e) => {
          e.preventDefault()
          run()
        }}
      >
        <input
          type="text"
          placeholder="Type an order line the way a customer would say it…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <button type="submit" className="btn btn-primary" disabled={test.isPending}>
          {test.isPending ? 'Matching…' : 'Match'}
        </button>
      </form>

      <div className="examples">
        <span className="muted">Try:</span>
        {EXAMPLES.map((ex) => (
          <button type="button" key={ex} className="chip" onClick={() => run(ex)}>
            {ex}
          </button>
        ))}
      </div>

      {error && <ErrorNote message={error} />}

      {result && (
        <div className="tester-result">
          <div className="result-head">
            <MatchBadge status={result.status} />
            <span className="confidence">{(result.confidence * 100).toFixed(0)}% confident</span>
          </div>

          {result.matched ? (
            <div className="result-match">
              <div className="result-name">{result.matched.item_name}</div>
              <div className="mono muted">{result.matched.item_code}</div>
            </div>
          ) : (
            <div className="result-match missing">No product matched</div>
          )}

          <div className="result-parse">
            <div>
              <span className="line-label">Normalized</span>
              <code>{result.normalized}</code>
            </div>
            <div>
              <span className="line-label">Dimensions read</span>
              <code>
                {Object.keys(result.parsed_spec).length
                  ? JSON.stringify(result.parsed_spec)
                  : 'none'}
              </code>
            </div>
          </div>

          {result.candidates.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Code</th>
                  <th className="right">Score</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                {result.candidates.map((c) => (
                  <tr key={c.product_id}>
                    <td>{c.item_name}</td>
                    <td className="mono">{c.item_code}</td>
                    <td className="right">{(c.score * 100).toFixed(0)}%</td>
                    <td className="muted">{c.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
